import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, Wrench, FileText, Users, Calendar, CreditCard, Factory } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECCIONES = [
  { key: "users_sin_empresa", label: "Usuarios sin empresa", icon: Users, severidad: "warn" },
  { key: "empresas_sin_dueno", label: "Empresas sin dueño", icon: Users, severidad: "error" },
  { key: "empresas_sin_plan", label: "Empresas sin plan", icon: FileText, severidad: "warn" },
  { key: "contratos_huerfanos", label: "Contratos huérfanos", icon: FileText, severidad: "warn" },
  { key: "contratos_duplicados", label: "Contratos duplicados", icon: FileText, severidad: "error" },
  { key: "pagos_huerfanos", label: "Pagos huérfanos", icon: CreditCard, severidad: "error" },
  { key: "pagos_saldo_negativo", label: "Pagos saldo negativo", icon: CreditCard, severidad: "warn" },
  { key: "eventos_duplicados", label: "Eventos duplicados", icon: Calendar, severidad: "warn" },
  { key: "eventos_fantasma", label: "Eventos fantasma", icon: Calendar, severidad: "error" },
  { key: "producciones_huerfanas", label: "Producciones huérfanas", icon: Factory, severidad: "warn" },
  { key: "inconsistencias_financieras", label: "Inconsistencias financieras", icon: CreditCard, severidad: "error" },
];

const AdminAuditoria = () => {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reparando, setReparando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/auditoria`);
      setRep(r.data);
    } catch (e) { toast.error("No se pudo cargar la auditoría"); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const reparar = async (dryRun) => {
    setReparando(true);
    try {
      const r = await axios.post(`${API}/admin/auditoria/reparar`, { dry_run: dryRun });
      const total = r.data.log.reduce((s, l) => s + (typeof l.afectados === "number" ? l.afectados : 0), 0);
      toast.success(`${dryRun ? "Simulación" : "Reparación"} completada — ${total} registros ${dryRun ? "afectables" : "corregidos"}`);
      if (!dryRun) await cargar();
    } catch (e) { toast.error(e.response?.data?.detail || "Error en reparación"); }
    finally { setReparando(false); }
  };

  if (loading) return <div className="p-6 text-sm" style={{ color: "var(--brand-text-muted)" }}>Auditando base de datos…</div>;
  if (!rep) return <div className="p-6">Sin reporte</div>;

  const resumen = rep.resumen || {};
  const totalProblemas = Object.values(resumen).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl" style={{ color: "var(--brand-text)" }}>Auditoría del sistema</h1>
          <p className="text-sm mt-1" style={{ color: "var(--brand-text-muted)" }}>
            <strong>¿Para qué sirve?</strong> Detecta inconsistencias en tu base de datos: usuarios sin empresa, contratos huérfanos, pagos con saldo negativo, eventos duplicados. Puedes simular la reparación antes de aplicarla.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={cargar} className="eo-btn eo-btn-outline" data-testid="btn-recargar-auditoria"><RefreshCw className="w-4 h-4" /> Refrescar</button>
          <button onClick={async () => {
            try {
              const r = await axios.post(`${API}/catalogo/auto-etiquetar`, { dry_run: false });
              const tot = Object.values(r.data.resultados).reduce((s, c) => s + (c.petit || 0) + (c.kermesita || 0) + (c.bocatto || 0) + (c.compartido || 0), 0);
              toast.success(`${tot} items del catálogo etiquetados por unidad`);
            } catch (e) { toast.error("Error al auto-etiquetar"); }
          }} className="eo-btn eo-btn-outline" data-testid="btn-auto-etiquetar">Auto-etiquetar catálogo</button>
          <button onClick={() => reparar(true)} disabled={reparando} className="eo-btn eo-btn-outline" data-testid="btn-simular-reparar">Simular reparación</button>
          <button onClick={() => { if (window.confirm("¿Ejecutar reparación REAL? Marcará eventos duplicados, sincronizará contratos y asignará huérfanos a Celebra Petit.")) reparar(false); }} disabled={reparando} className="eo-btn eo-btn-primary" data-testid="btn-reparar-real"><Wrench className="w-4 h-4" /> Reparar ahora</button>
        </div>
      </div>

      {/* Banner estado */}
      <div className="eo-card p-5 flex items-center gap-4">
        {totalProblemas === 0 ? (
          <><CheckCircle2 className="w-10 h-10" style={{ color: "var(--brand-success)" }} />
            <div>
              <p className="font-display text-xl" style={{ color: "var(--brand-text)" }}>Sistema saludable</p>
              <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>No se detectaron inconsistencias.</p>
            </div></>
        ) : (
          <><AlertTriangle className="w-10 h-10" style={{ color: "#d97706" }} />
            <div>
              <p className="font-display text-xl" style={{ color: "var(--brand-text)" }}>{totalProblemas} registros requieren atención</p>
              <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Ejecuta &quot;Reparar ahora&quot; para sincronizar y consolidar la base.</p>
            </div></>
        )}
      </div>

      {/* Totales generales */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {Object.entries(rep.totales || {}).map(([k, v]) => (
          <div key={k} className="eo-card p-3 text-center">
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-text-muted)" }}>{k}</p>
            <p className="font-display text-2xl mt-1" style={{ color: "var(--brand-text)" }}>{v}</p>
          </div>
        ))}
      </div>

      {/* Issues por sección */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SECCIONES.map(s => {
          const items = rep[s.key] || [];
          const n = items.length;
          const color = n === 0 ? "var(--brand-success)" : s.severidad === "error" ? "#dc2626" : "#d97706";
          return (
            <div key={s.key} className="eo-card p-4" data-testid={`audit-${s.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <s.icon className="w-4 h-4" style={{ color }} />
                  <p className="font-medium text-sm" style={{ color: "var(--brand-text)" }}>{s.label}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: n === 0 ? "var(--brand-success-soft)" : "var(--brand-surface-muted)", color }}>{n}</span>
              </div>
              {n > 0 && (
                <div className="text-xs space-y-1 max-h-40 overflow-y-auto" style={{ color: "var(--brand-text-muted)" }}>
                  {items.slice(0, 8).map((it, i) => (
                    <div key={i} className="p-1.5 rounded" style={{ background: "var(--brand-surface-muted)" }}>
                      {typeof it === "string" ? it : JSON.stringify(it).substring(0, 140)}
                    </div>
                  ))}
                  {n > 8 && <p className="text-[10px] italic">… y {n - 8} más</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminAuditoria;
